import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";

interface Props {
  workflowId: string;
  approvalId: string | undefined;
  open: boolean;
  onClose: () => void;
  onRejected: () => void;
}

const rejectRequest = withErrorHandling(adminApi.rejectInvestorChangeRequest);
const rejectApproval = withErrorHandling(adminApi.rejectApproval);

const RejectionConfirmationDialog = ({ workflowId, approvalId, open, onClose, onRejected }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");

  const handleRejectClick = async () => {
    const commentUserInput = comment.trim() || undefined;

    setLoading(true);

    let error: ApiError | undefined;
    if (approvalId) {
      const [, requestError] = await rejectApproval(approvalId, { commentUserInput });
      error = requestError;
    } else {
      const [, requestError] = await rejectRequest({ workflowId, commentUserInput });
      error = requestError;
    }

    setLoading(false);

    if (error) {
      logError(error, "[RejectionConfirmationDialog]");
      sendNotificationError(error.message);
      return;
    }

    onClose();
    sendNotification("The request was rejected");
    onRejected();
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Reject</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CancelDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Reject request?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              The proposed changes will be dismissed. This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
          <TextField
            multiline
            rows={4}
            spellCheck
            fullWidth
            placeholder="Add rejection reason..."
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleRejectClick}>
          Reject
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default RejectionConfirmationDialog;
